'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import Link from 'next/link';

export interface AppTopHeaderProfileMenuItem {
  label: string;
  icon: LucideIcon;
  href?: string;
  external?: boolean;
  onSelect?: () => void;
  content?: ReactNode;
}

interface AppTopHeaderProfileMenuProps {
  initials: string;
  items: AppTopHeaderProfileMenuItem[];
  className?: string;
}

export function AppTopHeaderProfileMenu({
  initials,
  items,
  className,
}: AppTopHeaderProfileMenuProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'bg-grey-100 text-neutral-200 dark:bg-grey-900 dark:text-neutral-100 flex size-[30px] cursor-pointer items-center justify-center rounded-full text-sm leading-5',
            'hover:bg-grey-200 focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none dark:hover:bg-grey-800',
            className
          )}
          aria-label="Open profile menu"
        >
          <p>{initials}</p>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={12}
        className="bg-grey-0 dark:bg-grey-1000 w-[250px] gap-6 rounded-4xl p-6"
      >
        <div className="flex flex-col gap-2">
          {items.map((item) => {
            if (item.content) {
              return <div key={item.label}>{item.content}</div>;
            }

            const Icon = item.icon;
            const content = (
              <>
                <Icon className="size-6 stroke-2!" />
                <p>{item.label}</p>
              </>
            );

            const className =
              'text-neutral-200 cursor-pointer font-medium dark:text-neutral-100 hover:bg-grey-50 dark:hover:bg-grey-900 flex h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm';

            if (item.href) {
              return (
                <Link
                  key={item.label}
                  href={item.href!}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noreferrer' : undefined}
                  className={className}
                  onClick={item.onSelect}
                >
                  {content}
                </Link>
              );
            }

            return (
              <button key={item.label} type="button" className={className} onClick={item.onSelect}>
                {content}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
